import { AlertCircle } from "lucide-react";
import type {
  CsvCoercionError,
  CsvValidationResponse,
  ImportRowError,
} from "@adva/types";
import { Button } from "@/components/ui/button";
import { StepCounter } from "../components/step-counter";

interface StepValidateProps {
  validation: CsvValidationResponse;
  unmappedCount: number;
  onBack: () => void;
  onContinue: () => void;
}

export function StepValidate({
  validation,
  unmappedCount,
  onBack,
  onContinue,
}: StepValidateProps) {
  const { preview, errors, coercion_errors } = validation;
  const previewHeaders = Object.keys(preview[0] ?? {});
  const hasBlockingErrors = errors.length > 0;

  return (
    <div className="space-y-5">
      <StepCounter current={3} total={4} label="Validate" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Total rows" value={validation.total_rows} />
        <Stat label="Valid rows" value={validation.valid_rows} tone="success" />
        <Stat
          label="Error rows"
          value={validation.error_rows}
          tone={validation.error_rows > 0 ? "error" : "default"}
        />
        <Stat
          label="Unmapped"
          value={unmappedCount}
          tone={unmappedCount > 0 ? "warn" : "default"}
        />
      </div>

      {errors.length > 0 && <RowErrorsPanel errors={errors} />}
      {coercion_errors.length > 0 && (
        <CoercionErrorsPanel errors={coercion_errors} />
      )}

      {preview.length > 0 && (
        <div>
          <h3 className="typo-label text-[color:var(--color-text-md)] mb-2">
            First {preview.length} rows
          </h3>
          <div className="overflow-x-auto rounded-lg border border-[color:var(--color-border)]">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--color-elevated)]">
                <tr>
                  {previewHeaders.map((h) => (
                    <th
                      key={h}
                      className="border-b border-[color:var(--color-border)] px-3 py-2 text-left font-medium text-[color:var(--color-text-hi)]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-[color:var(--color-border)] last:border-b-0"
                  >
                    {previewHeaders.map((h) => (
                      <td
                        key={h}
                        className="px-3 py-2 text-[color:var(--color-text-md)] truncate max-w-xs"
                      >
                        {formatCell(row[h])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-between border-t border-[color:var(--color-border)] pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={hasBlockingErrors}
          onClick={onContinue}
          title={
            hasBlockingErrors ? "Fix row errors before continuing" : undefined
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "warn" | "error";
}) {
  const toneClass =
    tone === "success"
      ? "text-[color:var(--color-success)]"
      : tone === "warn"
        ? "text-[color:var(--color-warning)]"
        : tone === "error"
          ? "text-[color:var(--color-danger)]"
          : "text-[color:var(--color-text-hi)]";
  return (
    <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3">
      <div className="typo-label text-[color:var(--color-text-md)]">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function RowErrorsPanel({ errors }: { errors: ImportRowError[] }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-danger)]">
        <AlertCircle className="h-4 w-4" />
        {errors.length} row error{errors.length === 1 ? "" : "s"}
      </div>
      <ul className="space-y-1 text-xs">
        {errors.slice(0, 10).map((err, i) => (
          <li key={i}>
            <span className="typo-mono">row {err.row}</span>
            {err.field && <> · {err.field}</>}: {err.message}
          </li>
        ))}
        {errors.length > 10 && (
          <li className="text-[color:var(--color-text-md)]">
            …and {errors.length - 10} more
          </li>
        )}
      </ul>
    </div>
  );
}

function CoercionErrorsPanel({ errors }: { errors: CsvCoercionError[] }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-warning)]">
        <AlertCircle className="h-4 w-4" />
        {errors.length} coercion issue{errors.length === 1 ? "" : "s"}
      </div>
      <ul className="space-y-1 text-xs text-[color:var(--color-warning)]">
        {errors.slice(0, 10).map((err, i) => (
          <li key={i}>
            <span className="typo-mono">
              row {err.row} · {err.column}
            </span>
            : {err.reason}
            {err.value !== null && (
              <>
                {" "}
                <span className="typo-mono">({err.value})</span>
              </>
            )}
          </li>
        ))}
        {errors.length > 10 && (
          <li className="text-[color:var(--color-text-md)]">
            …and {errors.length - 10} more
          </li>
        )}
      </ul>
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
